import React from 'react';
import classNames from 'classnames';
import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { AspectRatioWrapper, NamedLink, ResponsiveImage } from '../../components';
import Icons from '../Icons/Icons';
import css from './ListingCard.module.css';
import { updateProfile } from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

const MIN_LENGTH_FOR_LONG_WORDS = 10;
const featuredUsers = process.env.REACT_APP_FEATURED_USERS;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  }
  if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, {
  loadAfterInitialRendering: 3000,
});

const PriceMaybe = ({ price, publicData, config, intl }) => {
  const listingType = publicData?.listingType;
  const foundConfig = config.listing.listingTypes.find(conf => conf.listingType === listingType);
  if (!displayPrice(foundConfig) && price) return null;

  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);

  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable && (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      )}
    </div>
  );
};

export const ListingCard = props => {
  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    isPractionarListingCard,
    showAuthorInfo = true,
  } = props;

  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const dispatch = useDispatch();
  const history = useHistory();

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;

  const firstImage = currentListing.images?.[0] || null;
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage.attributes.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActiveProps = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : {};

  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const currentUser = useSelector(state => state.user.currentUser);
  const cartItems = currentUser?.attributes?.profile?.publicData?.cartItems ?? [];
  const favorites = currentUser?.attributes?.profile?.publicData?.favorites ?? [];

  const isOwnListing = currentUser?.id && author.id?.uuid === currentUser?.id?.uuid;

  const isInCart = cartItems.includes(id);
  const isFav = favorites.includes(id);

  const toggleProfileArray = (key, list, itemId) => {
    const updated = list.includes(itemId) ? list.filter(i => i !== itemId) : [...list, itemId];
    dispatch(
      updateProfile({
        publicData: {
          [key]: updated,
        },
      })
    );
  };

  const handleToggleCart = () => {
    if (!isAuthenticated) {
      history.push('/login', { from: '/s' });
    }
    toggleProfileArray('cartItems', cartItems, id);
  };

  const canSeeFeaturedBadge = author?.id?.uuid === '697109f0-1616-42d6-9044-0f4369b107e7';

  const handleFavorites = () => {
    if (!isAuthenticated) {
      history.push('/login', { from: '/s' });
    }
    toggleProfileArray('favorites', favorites, id);
  };

  const rootClasses = classNames(
    rootClassName || css.root,
    className,
    isPractionarListingCard && css.practionarListingCard
  );

  const allFeaturedUsers = featuredUsers ? featuredUsers?.split(',')?.map(id => id?.trim()) : [];
  const isFeatured = allFeaturedUsers.includes(author.id.uuid);

  return (
    <div className={rootClasses} {...setActiveProps}>
      <NamedLink className={rootClasses} name="ListingPage" params={{ id, slug }}>
        {canSeeFeaturedBadge ? (
          <div className={css.available}>
            <Icons name="soldTag" /> SWM
            {/* <FormattedMessage id="ListingCard.Featured" /> */}
          </div>
        ) : null}

        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
          {...setActiveProps}
        >
          <LazyImage
            rootClassName={css.rootForImage}
            alt={title}
            image={firstImage}
            variants={variants}
            sizes={renderSizes}
          />

          {!isOwnListing && (
            <div
              className={classNames(css.favorite, isFav ? css.filled : css.unfilled)}
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                handleFavorites();
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
                e.preventDefault();
                handleFavorites();
              }}
              role="button"
              tabIndex={0}
              aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
            >
              <Icons name={isFav ? 'favorite' : 'unfavorite'} />
            </div>
          )}
        </AspectRatioWrapper>

        {isPractionarListingCard ? (
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
        ) : (
          <div className={css.info}>
            <div className={css.mainInfo}>
              <div className={css.title}>
                {richText(title, {
                  longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                  longWordClass: css.longWord,
                })}
              </div>
              <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />
            </div>

            {showAuthorInfo && (
              <div className={css.authorInfo}>
                <FormattedMessage id="ListingCard.author" values={{ authorName }} />
              </div>
            )}

            {!isOwnListing && (
              <div className={css.bottomInfo}>
                <div
                  className={css.cartIconWrapper}
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleToggleCart();
                  }}
                >
                  {isInCart ? (
                    <span className={css.addedToCart}>
                      Added to Cart <Icons name="bagFilled" />
                    </span>
                  ) : (
                    <span className={css.addToCart}>
                      Add to Cart <Icons name="bagEmpty" />
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </NamedLink>
    </div>
  );
};

export default ListingCard;
